<template>
    <div>
        <b-row>
            <b-col cols="5">
                <h3 class="mb-2">Inventory</h3>
                <b-button variant="primary" size="sm" @click.prevent="openForm">
                    <span>New Inventory</span> <i class="fas fa-plus-circle"></i>
                </b-button>
            </b-col>
        </b-row>

        <b-modal ref="editModal" :title="inventory.id ? 'Edit Inventory' : 'Add Inventory'" @ok="submitInformation"
            no-fade centered>
            <b-row>
                <b-col cols="12">
                    <SearchComponent :id="Number(store.id)" v-model="store" :label="`Store`" prepopulate
                        :search-u-r-l="`/Store`" :disabled="!!inventory.id" />
                </b-col>
                <b-col cols="12">
                    <SearchComponent :id="Number(program.id)" v-model="program" clearable label="Program"
                        :params="{ flags: 'Consigned Inventory' }" prepopulate search-u-r-l="/Program"
                        :disabled="!!inventory.id" />
                </b-col>
                <b-col v-if="hasInventoryLocations" cols="12">
                    <b-checkbox v-model="useForFilling" switch class="pull-right">Use for Filling</b-checkbox>
                    <SearchComponent :id="location?.id" v-model="location" search-u-r-l="/inventory/inventory-locations"
                        prepopulate label="Location" clearable :disabled="!!inventory.id" />
                </b-col>
                <b-col cols="12">
                    <b-form-group label="Quantity">
                        <b-input v-model.number="inventory.quantity" type="number" />
                    </b-form-group>
                </b-col>
                <b-col cols="12">
                    <b-form-group label="Lot Number (optional)">
                        <b-input v-model="inventory.lotNumber" placeholder="Leave blank for non-lot inventory"
                            @change="lookupSiblingCoa" />
                    </b-form-group>
                </b-col>
                <b-col cols="12">
                    <b-form-group label="Expiration Date (optional)">
                        <b-input v-model="inventory.expirationDate" type="date" />
                    </b-form-group>
                </b-col>
                <b-col cols="12">
                    <b-form-group label="Cost Per Unit (optional)">
                        <b-input v-model.number="inventory.costPerUnit" type="number" step="0.01" />
                    </b-form-group>
                </b-col>
                <b-col cols="12" v-if="showCoaFields">
                    <b-form-group label="Certificate of Analysis (optional)" class="mb-0">
                        <CoaFieldsComponent :assay-percent.sync="inventory.assayPercent"
                            :assay-basis.sync="inventory.assayBasis" :lod-percent.sync="inventory.lodPercent"
                            :bounds="activityFactorSettings.bounds"
                            :decimal-places="activityFactorSettings.decimalPlaces" :sibling="siblingCoa"
                            :sibling-prefilled="siblingPrefilled" :fixed-factor-usages="fixedFactorUsages" />
                    </b-form-group>
                </b-col>
                <b-col cols="12" v-if="statusOptions.length">
                    <b-form-group label="Status">
                        <b-form-select v-model="inventory.status" :options="statusOptions" />
                    </b-form-group>
                </b-col>
                <b-col cols="12" v-if="drugSource === 2">
                    <b-checkbox v-model="adjustIngredients" switch class="pull-right">
                        Also Adjust Ingredient Inventory
                    </b-checkbox>
                </b-col>
                <b-col cols="12" v-if="reasonOptions.length">
                    <b-form-group label="Reason">
                        <b-form-select v-model="reasonSelection" :options="reasonOptions" />
                    </b-form-group>
                </b-col>
            </b-row>
        </b-modal>

        <b-row id="inventory-table-row">
            <b-col>
                <b-table id="inventory-table" :fields="inventoryFields" :items="inventories"
                    :sort-by.sync="sortBy" :sort-desc.sync="sortDesc">
                    <template #cell(location.name)="data">
                        <div>
                            {{ data.value }}
                        </div>
                        <b-badge variant="primary" v-if="data.item.useForFilling">
                            Use for Filling
                        </b-badge>
                    </template>
                    <template #cell(status)="data">
                        <b-badge v-if="data.value" :variant="isStatusEligible(data.value) ? 'success' : 'danger'">
                            {{ data.value }}
                        </b-badge>
                    </template>
                    <template #cell(actions)="data">
                        <div class="text-right table-icon-actions">
                            <a href="#" @click.prevent="viewHistory(data.item)" v-b-tooltip="'History'">
                                <i class="fas fa-history" />
                            </a>
                            <a href="#" @click.prevent="updateInventory(data.item)" v-b-tooltip="'Edit'">
                                <b-icon icon="pencil-square" />
                            </a>
                            <a href="#" @click.prevent="deleteInventoryConfirmDialog(data.item)" v-b-tooltip="'Remove'">
                                <b-icon icon="x-circle-fill" variant="danger" />
                            </a>
                        </div>
                    </template>
                </b-table>
            </b-col>
        </b-row>
        <b-modal v-model="showHistory" size="xl" hide-footer
            :title="`Inventory History at ${store.name}${program?.name?.length > 0 ? ' - ' + program.name : ''}`" no-fade>
            <InventoryHistory id="inventory-history-component" :drug="drug" :store="store" :program="program" />
        </b-modal>
    </div>
</template>

<script lang="ts">
import { ASSAY_BASIS_LABELS, FixedFactorIngredientUsage, Inventory, LotCoaSibling } from '@/models/Inventory';
import { Store } from '@/models/Store';
import { Program } from '@/models/Program';
import { Component, Prop, Vue, Ref, Watch } from 'vue-property-decorator';
import axios from 'axios';
import SearchComponent from '@/components/SearchComponent.vue';
import CoaFieldsComponent from '@/components/Inventory/CoaFieldsComponent.vue';
import { NotificationOptions } from '@/util/NotificationOptionsPresets';
import InventoryHistory from "@/components/Inventory/InventoryHistory.vue";
import { DrugProduct } from "@/models/Drug/DrugProduct";
import { InventoryLocation } from '@/models/InventoryLocation';
import { BModal } from 'bootstrap-vue';
import moment from 'moment';
import {
    ACTIVITY_FACTOR_SETTINGS_OFF,
    ActivityFactorSettings,
    fetchFixedFactorIngredientUsages,
    fetchLotCoaSibling,
    loadActivityFactorSettings,
} from '@/util/activityFactorApi';
import { assayBasisAppliesLod, hasAnyCoaValue, hasCoa } from '@/util/activityFactor';

@Component({
    name: "InventoryComponent",
    components: {
        InventoryHistory,
        SearchComponent,
        CoaFieldsComponent
    }
})
export default class InventoryComponent extends Vue {

    @Prop({ default: 0 }) private drugSource!: Number;
    @Prop() private packageId!: string;
    @Prop() private billingUnit!: string;
    @Prop() private inventories!: Inventory[];

    mounted() {
        this.getReasonOptionsFromConfiguration();
        this.loadHasInventoryLocations();
        this.loadLotStatuses();
        this.loadActivityFactorSettings();
    }

    protected showHistory: boolean = false;

    private sortBy: string = "storeName";
    private sortDesc: boolean = false;

    private store: Store = new Store();
    private program: Program = new Program();

    private inventory: Inventory = InventoryComponent.blankInventory();
    protected reasonSelection: string | null = null;
    protected location = new InventoryLocation();
    protected useForFilling = false;
    protected adjustIngredients = false;
    @Ref("editModal") protected editModal!: BModal;

    protected reasonOptions: any[] = [];
    protected lotStatuses: { name: string; isEligibleForFilling: boolean }[] = [];
    protected statusOptions: any[] = [];

    protected activityFactorSettings: ActivityFactorSettings = ACTIVITY_FACTOR_SETTINGS_OFF;
    /** Another row for the same drug and lot number that carries COA, once the lot number is known. */
    protected siblingCoa: LotCoaSibling | null = null;
    protected siblingPrefilled = false;
    protected fixedFactorUsages: FixedFactorIngredientUsage[] = [];

    /**
     * The COA keys are declared up front so Vue observes them: the modal binds them with `.sync`
     * and the live factor preview only re-renders when the assignment hits a reactive setter.
     */
    private static blankInventory(): Inventory {
        return new Inventory({ assayPercent: null, assayBasis: null, lodPercent: null } as Inventory);
    }

    get showCoaFields(): boolean {
        return this.activityFactorSettings.enabled;
    }

    async loadActivityFactorSettings() {
        this.activityFactorSettings = await loadActivityFactorSettings();
        await this.loadFixedFactorUsages();
    }

    @Watch('packageId')
    async loadFixedFactorUsages() {
        if (!this.activityFactorSettings.enabled || !Number(this.packageId)) {
            this.fixedFactorUsages = [];
            return;
        }
        this.fixedFactorUsages = await fetchFixedFactorIngredientUsages(Number(this.drugSource), Number(this.packageId));
    }

    /**
     * Looks up another row for the same lot that already carries COA. When the form's COA is still
     * blank the sibling's values are copied in (and labelled as such); otherwise the sibling is only
     * kept so the form can warn if the values being entered disagree with it.
     */
    async lookupSiblingCoa() {
        this.siblingPrefilled = false;
        if (!this.showCoaFields) {
            this.siblingCoa = null;
            return;
        }
        const lotNumber = this.inventory.lotNumber;
        const sibling = await fetchLotCoaSibling(Number(this.drugSource), Number(this.packageId), lotNumber, this.inventory.id);
        // The lot number may have changed again while the request was in flight.
        if ((this.inventory.lotNumber ?? '').trim() !== (lotNumber ?? '').trim()) return;
        this.siblingCoa = sibling;
        if (sibling && !hasAnyCoaValue(this.inventory)) {
            this.inventory.assayPercent = sibling.assayPercent;
            this.inventory.assayBasis = sibling.assayBasis;
            this.inventory.lodPercent = sibling.lodPercent;
            this.siblingPrefilled = true;
        }
    }

    async getReasonOptionsFromConfiguration() {
        try {
            const response = await this.$http.get<Array<String>>("/Configuration/InventoryReasons");
            if (response.data && !response.data.length) return;
            this.reasonOptions = [
                { value: null, text: 'Select a reason' },
                ...(response.data ? response.data : ['New Inventory', 'Stock Check'])
            ];
        } catch {
            // ignore
        }
    }

    async loadLotStatuses() {
        try {
            const response = await this.$http.get<string>("/Configuration/InventoryLotStatuses");
            if (response.data) {
                this.lotStatuses = typeof response.data === 'string'
                    ? JSON.parse(response.data)
                    : response.data;
                if (this.lotStatuses.length) {
                    this.statusOptions = [
                        { value: null, text: 'No Status' },
                        ...this.lotStatuses.map(s => ({ value: s.name, text: s.name }))
                    ];
                }
            }
        } catch (error) {
            this.$notification(NotificationOptions.error(error));
            console.error("Failed to load inventory lot statuses.", error);
        }
    }

    isStatusEligible(statusName: string): boolean {
        const status = this.lotStatuses.find(s => s.name === statusName);
        return status?.isEligibleForFilling ?? false;
    }

    get hasStatuses(): boolean {
        return this.lotStatuses.length > 0 || this.inventories.some(i => i.status != null);
    }

    get inventoryFields() {
        const fields = [
            { key: "programName", label: "Program", sortable: true },
            { key: "storeName", label: "Store", sortable: true },
            { key: "lotNumber", label: "Lot #", visible: this.hasLotTracking, sortable: true },
            { key: "expirationDate", label: "Expiration", visible: this.hasLotTracking, formatter: (value: any) => value ? moment(value).format("L") : null, sortable: true },
            { key: "costPerUnit", label: "Cost", visible: this.hasLotTracking, sortable: true },
            { key: "coa", label: "COA", visible: this.showCoaFields && this.inventories.some(i => hasCoa(i)), formatter: this.coaFormatter },
            { key: "status", label: "Status", visible: this.hasStatuses, sortable: true },
            { key: "reason", label: "Reason", visible: !!this.inventories.filter(i => i.reason).length, sortable: true },
            { key: "location.name", label: "Location", visible: this.hasInventoryLocations, sortable: true },
            { key: "quantity", sortable: true },
            { key: "allocated", sortable: true },
            { key: "net", label: "Net", formatter: this.netFormatter, sortable: true, sortByFormatted: true },
            { key: "createdBy", label: "Created By", sortable: true },
            { key: "actions", label: "", class: "actions" },
        ];

        return fields.filter(f => f.visible !== false);
    }

    get drug(): DrugProduct {
        return {
            drugId: Number(this.packageId),
            source: this.drugSource,
        } as DrugProduct;
    }

    get hasLotTracking(): boolean {
        return this.inventories.some(i => i.lotNumber != null || i.expirationDate != null || i.costPerUnit != null);
    }

    netFormatter(_value: any, _key: number, item: Inventory) {
        return (item.quantity || 0) - (item.allocated || 0);
    }

    /** "99.7% Anhydrous, LOD 3.1%" for a lot carrying COA; blank otherwise. */
    coaFormatter(_value: any, _key: string, item: Inventory): string {
        if (!hasCoa(item)) return '';
        const basis = item.assayBasis ? ASSAY_BASIS_LABELS[item.assayBasis] : '';
        const lod = assayBasisAppliesLod(item.assayBasis) && item.lodPercent != null ? `, LOD ${item.lodPercent}%` : '';
        return `${item.assayPercent}% ${basis}${lod}`;
    }

    submitInformation(e: any) {
        e.preventDefault();

        this.inventory.drugSource = Number(this.drugSource);
        this.inventory.packageId = Number(this.packageId);
        this.inventory.billingUnit = this.billingUnit;
        this.inventory.storeId = Number(this.store.id);
        this.inventory.programId = Number(this.program.id);
        this.inventory.reason = this.reasonSelection;
        this.inventory.location = this.location;
        this.inventory.useForFilling = this.useForFilling;
        this.inventory.adjustIngredients = this.adjustIngredients;

        // Find existing inventory to update - lot numbers and expiration dates must match exactly
        if (!this.inventory.id) {
            const updateInventory = this.inventories.find(item =>
                (item.location?.id ?? 0) == (this.inventory.location?.id ?? 0) &&
                item.storeId == this.inventory.storeId &&
                (item.programId || 0) == (this.inventory.programId || 0) &&
                // Lot numbers must match exactly (null === null, or both have the same value)
                (item.lotNumber || null) === (this.inventory.lotNumber || null) &&
                // Expiration dates must match exactly
                (item.expirationDate || null) === (this.inventory.expirationDate || null)
            );
            if (updateInventory) {
                this.inventory.id = updateInventory.id;
                // The save overwrites the whole row, so a top-up entered without COA keeps the COA
                // the matched row already carries rather than clearing it.
                if (!hasAnyCoaValue(this.inventory)) {
                    this.inventory.assayPercent = updateInventory.assayPercent ?? null;
                    this.inventory.assayBasis = updateInventory.assayBasis ?? null;
                    this.inventory.lodPercent = updateInventory.lodPercent ?? null;
                }
            }
        }
        axios.post<Inventory>('/Inventory/', this.inventory)
            .then(response => {
                if (response.data?.id) {
                    this.$notification(NotificationOptions.successSaveNotificationPreset("Inventory"));
                    this.$emit("update");
                }
                this.clearForm();
                this.editModal.hide();
            })
            .catch(error => {
                this.$notification(NotificationOptions.errorSaveNotificationPreset("Inventory", error));
                console.error("Error while updating inventory.", { error, response: error?.response });
            });
    }

    viewHistory(item: any) {
        this.showHistory = true;
        this.store = new Store({ id: item.storeId, name: item.storeName } as Store);
        this.program = new Program({ id: item.programId, name: item.programName } as Program);
    }

    updateInventory(item: Inventory) {
        this.store.id = item.storeId;
        this.program.id = Number(item.programId || 0);
        this.inventory.id = item.id;
        this.inventory.quantity = item.quantity;
        this.inventory.lotNumber = item.lotNumber;
        this.inventory.expirationDate = item.expirationDate ? this.formatDateForInput(item.expirationDate) : null;
        this.inventory.costPerUnit = item.costPerUnit;
        this.inventory.status = item.status ?? null;
        // Copied whether or not the COA fields are shown: the save overwrites the whole row, so an
        // edit made with the feature off must not wipe COA that receiving or the API recorded.
        this.inventory.assayPercent = item.assayPercent ?? null;
        this.inventory.assayBasis = item.assayBasis ?? null;
        this.inventory.lodPercent = item.lodPercent ?? null;
        this.location = new InventoryLocation();
        this.location.id = item.location?.id ?? 0;
        this.useForFilling = item.useForFilling;
        this.siblingCoa = null;
        this.siblingPrefilled = false;
        this.editModal.show();
        this.lookupSiblingCoa();
    }

    formatDateForInput(date: string | null): string | null {
        if (!date) return null;
        const d = new Date(date);
        if (isNaN(d.getTime())) return null;
        const year = d.getFullYear();
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    }

    deleteInventoryConfirmDialog(item: Inventory) {
        this.$bvModal.msgBoxConfirm(`This inventory record associated to this drug will get deleted. Are you sure?`, {
            title: 'Confirm',
            size: 'sm',
            buttonSize: 'sm',
            okVariant: 'danger',
            okTitle: 'YES',
            cancelTitle: 'NO',
            footerClass: 'p-2',
            hideHeaderClose: false,
            centered: true
        })
            .then(value => {
                if (!value) return;
                this.deleteInventory(item);
            })
            .catch(err => {
                console.error("Error caught on Clear button.", err);
            });
    }

    deleteInventory(item: Inventory) {
        if (!item?.id) return;

        this.$http.delete(`inventory/${item.id}`)
            .then(res => {
                if (res.data) {
                    this.$notification(NotificationOptions.notificationOptionsPreset("Inventory record successfully deleted.", NotificationOptions.NotificationTypes.success));
                    this.$emit('removed');
                }
            })
            .catch(err => {
                this.$notification(NotificationOptions.error(err));
                console.error("Not possible to delete inventory record.", err);
            });
    }

    openForm() {
        this.clearForm();
        this.editModal.show();
    }

    clearForm() {
        this.inventory = InventoryComponent.blankInventory();
        this.store = new Store();
        this.program = new Program();
        this.adjustIngredients = false;
        this.siblingCoa = null;
        this.siblingPrefilled = false;
    }

    protected hasInventoryLocations = false;

    async loadHasInventoryLocations() {
        try {
            this.hasInventoryLocations = (await axios.get<boolean>("/inventory/has-inventory-locations")).data;
        } catch {
            //ignore
        }
    }

}
</script>

<style scoped>
::v-deep(.actions) {
    width: 100px;
}
</style>
